import { isNil } from "lodash";
import { useInjection } from "@linkfluence/core/di";
import {
  isOfflineMediaPlatform,
  isWebPlatform,
} from "@linkfluence/models/platform";
import { useQuery } from "@tanstack/react-query";
import { useBlockedAuthors } from "@radarly/core/project/use-cases";
import {
  FullInfluencer,
  InfluencerLensFactory,
} from "@radarly/models/influencer";
import { BlockedAuthors } from "@radarly/models/project";
import {
  InfluencerRepository,
  InfluencerRepositoryUID,
} from "../repositories/InfluencerRepository";

export function useFullInfluencer(projectId: number, influencerId: string) {
  const repository = useInjection<InfluencerRepository>(
    InfluencerRepositoryUID
  );

  const getAuthorId = (author: FullInfluencer): string => {
    if (author) {
      const influencerLens = InfluencerLensFactory.create(author.user);
      return influencerLens.getId();
    }
    return null;
  };

  const isAuthorBlocked = (
    author: FullInfluencer,
    blockedAuthors: BlockedAuthors
  ): boolean => {
    if (author && blockedAuthors) {
      const authorId = getAuthorId(author);
      const platform = author.user.platform;
      if (isOfflineMediaPlatform(platform) || !blockedAuthors[platform]) {
        return false;
      }
      return blockedAuthors[platform].some((author) => {
        if (isWebPlatform(platform)) {
          return author.reversedUrl === authorId;
        }
        return author.id === authorId;
      });
    }
    return false;
  };

  const { blockedAuthors, isLoading: isLoadingBlockedAuthors } =
    useBlockedAuthors(projectId);

  const isValidId = !isNil(influencerId) && influencerId !== "";

  const {
    data: author,
    isLoading: isLoadingAuthor,
    isError,
  } = useQuery(
    ["/influencer/get", { projectId, influencerId }],
    () => {
      return influencerId
        ? repository.getInfluencerFromUid(projectId, influencerId)
        : null;
    },
    { enabled: isValidId } // Prevents making a request with an invalid influencer id
  );

  const isLoading = isLoadingAuthor || isLoadingBlockedAuthors;

  return {
    author: isValidId ? author : null,
    isLoading: isValidId && isLoading,
    isError: isError || isAuthorBlocked(author, blockedAuthors),
  };
}
